import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, AlarmClock, Flame, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { FollowupItem } from "@/components/followup/FollowupItem";
import { crmService } from "@/services/crmService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { FOLLOWUP_VIEWS, leadTypeLabel } from "@/config/crmOptions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function FollowUps() {
  const navigate = useNavigate();
  const { openAddFollowup } = useCrmActions();
  const [view, setView] = useState("overdue");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [attention, setAttention] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMeta = useCallback(async () => {
    const [summary, att] = await Promise.all([crmService.followupSummary(), crmService.followupAttention()]);
    setCounts(summary);
    setAttention(att);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { followups } = await crmService.listFollowups({ view });
      setItems(followups);
    } catch (e) {
      toast.error("Could not load follow-ups");
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const h = () => { loadMeta(); loadList(); };
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [loadMeta, loadList]);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="followups-page">
        <button onClick={() => navigate("/crm")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="followups-back">
          <ArrowLeft className="h-4 w-4" /> Back to CRM
        </button>

        <PageHeader
          eyebrow="AgentHQ · Sell"
          title="Smart Follow-Up"
          description="See who needs follow-up, what's overdue, and what's coming up. AgentHQ organizes the workflow — it never contacts clients for you."
        />

        {/* Deterministic attention rules */}
        {attention.length > 0 && (
          <div className="rounded-xl border border-amber-300/50 bg-amber-50/60 p-4" data-testid="followup-attention">
            <div className="mb-3 flex items-center gap-2">
              <Flame className="h-4 w-4 text-amber-600" />
              <h3 className="text-sm font-bold text-foreground">Needs attention</h3>
              <Badge variant="secondary">{attention.length}</Badge>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {attention.slice(0, 8).map((a) => (
                <div key={a.lead_id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2" data-testid={`attention-${a.lead_id}`}>
                  <div className="min-w-0">
                    <button className="truncate text-sm font-medium text-foreground hover:text-accent" onClick={() => a.contact?.id && navigate(`/crm/${a.contact.id}`)}>
                      {a.contact?.first_name} {a.contact?.last_name}
                    </button>
                    <p className="text-xs text-muted-foreground">
                      {leadTypeLabel(a.lead_type)} · {a.stage || "—"} ·{" "}
                      <span className={a.severity === "high" ? "font-medium text-red-600" : "text-amber-700"}>{a.label}</span>
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openAddFollowup(a.contact, { id: a.lead_id, lead_type: a.lead_type })} data-testid={`attention-add-${a.lead_id}`}>
                    <CalendarClock className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View tabs */}
        <div className="flex flex-wrap gap-1 border-b border-border" data-testid="followup-view-tabs">
          {FOLLOWUP_VIEWS.map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} data-testid={`followup-view-${v.key}`}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${view === v.key ? "border-brand-gold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {v.key === "overdue" && <AlarmClock className="h-3.5 w-3.5" />}
              {v.label}
              {counts[v.key] != null && counts[v.key] > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${v.key === "overdue" ? "bg-red-100 text-red-700" : "bg-secondary text-muted-foreground"}`}>{counts[v.key]}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10"><LoadingState label="Loading follow-ups" /></div>
        ) : items.length === 0 ? (
          <EmptyState
            title={view === "completed" ? "Nothing completed yet" : "You're all caught up"}
            description={view === "overdue" ? "No overdue follow-ups. Nice work staying on top of your relationships." : "No follow-ups in this view."}
          />
        ) : (
          <div className="space-y-2" data-testid="followup-list">
            {items.map((f) => <FollowupItem key={f.id} followup={f} onChanged={() => { loadMeta(); loadList(); }} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}
